import { useCallback, useRef } from "react";

import { useStatusIndicator } from "../../../roles/hooks/useStatusIndicator";
import type { StatusType } from "../../../../components/status-indicator/StatusIndicator";

export interface TaskBoardSaveStatusHandle {
  /** 保存処理の開始を通知する（in-flight カウンタを増やす） */
  onSaveStart: () => void;
  /** 保存処理の終了を通知する（`ok=false` で失敗扱い） */
  // eslint-disable-next-line no-unused-vars -- 関数シグネチャ引数名はドキュメント目的
  onSaveEnd: (ok: boolean) => void;
}

/**
 * タスクボード横断ビューの保存状態を StatusIndicator へ集約するフック。
 *
 * 複数の TaskBoardSection / ガントから保存（mutation）が並行して走るため、
 * in-flight カウンタで「最初の保存開始 → saving」「全保存完了 → saved / error」
 * にまとめ、チェック連打などでの表示チラつきを防ぐ。
 */
export function useTaskBoardSaveStatus(): TaskBoardSaveStatusHandle & {
  status: StatusType;
} {
  const { status, indicate } = useStatusIndicator();
  const inFlightRef = useRef(0);
  const hadErrorRef = useRef(false);

  const onSaveStart = useCallback(() => {
    inFlightRef.current += 1;
    if (inFlightRef.current === 1) hadErrorRef.current = false;
    indicate("saving");
  }, [indicate]);

  const onSaveEnd = useCallback(
    (ok: boolean) => {
      if (!ok) hadErrorRef.current = true;
      inFlightRef.current = Math.max(0, inFlightRef.current - 1);
      if (inFlightRef.current === 0) {
        indicate(hadErrorRef.current ? "error" : "saved");
      }
    },
    [indicate],
  );

  return { status, onSaveStart, onSaveEnd };
}
